'use client';

import { useEffect, useState } from 'react';
import Layout from '@/components/layout/Layout';
import DefaultPage from '@/components/templates/DefaultPage';
import { pageAPI } from '@/lib/api';

interface MediaItem {
  id: string;
  filename: string;
  originalName?: string;
  fileUrl?: string;
  description?: string;
  mimeType?: string;
  fileSize?: number;
  createdAt: string;
}

export default function DownloadPage() {
  const [pageContent, setPageContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [downloads, setDownloads] = useState<MediaItem[]>([]);
  const [downloadsLoading, setDownloadsLoading] = useState(true);

  useEffect(() => {
    const loadPageContent = async () => {
      try {
        const response = await pageAPI.getBySlug('download');
        const pageData = response.data;
        
        const col70Section = pageData.sections?.find(
          (section) => section.settings?.elementorCol === '70' && section.visible
        );
        
        if (col70Section?.content) {
          setPageContent(col70Section.content);
        } else if (pageData.content) {
          setPageContent(pageData.content);
        }
      } catch (error) {
        console.error('加载下载页面内容失败:', error);
        setPageContent('');
      } finally {
        setLoading(false);
      }
    };

    loadPageContent();
  }, []);

  useEffect(() => {
    const fetchDownloads = async () => {
      setDownloadsLoading(true);
      try {
        const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
        // 确保apiBaseUrl不包含/api后缀
        const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
        const response = await fetch(`${baseUrl}/api/media?limit=100`);
        const data = await response.json();
        
        if (data.data && Array.isArray(data.data)) {
          const allMedia = data.data || [];
          const docs = allMedia.filter((item: any) => 
            item.fileType === 'other' || 
            (item.mimeType && !item.mimeType.startsWith('image/') && !item.mimeType.startsWith('video/'))
          );
          setDownloads(docs);
          console.log('下载列表获取成功:', docs.length, '个文件');
        } else {
          console.log('下载列表数据格式不正确:', data);
        }
      } catch (error) {
        console.error('获取下载列表失败:', error);
      } finally {
        setDownloadsLoading(false);
      }
    };

    fetchDownloads();
  }, []);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-CN');
  };

  return (
    <Layout>
      <DefaultPage
        title="Download Center"
        subtitle="Download product information, technical documents and promotional materials"
        layout="two-column"
        leftColumn={
          <>
            {pageContent && (
              <div 
                className="elementor-widget-wrap mb-8"
                dangerouslySetInnerHTML={{ __html: pageContent }}
              />
            )}
            
            {downloads.length > 0 && (
              <div className="download-list">
                <h3 className="text-xl font-bold mb-4" style={{ color: '#1D2A5D' }}>download available</h3>
                <div className="space-y-4">
                  {downloads.map((item) => (
                    <div 
                      key={item.id} 
                      className="download-item"
                      style={{
                        padding: '16px',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>
                          {item.originalName || item.filename}
                        </h4>
                        {item.description && (
                          <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>
                            {item.description}
                          </p>
                        )}
                        <div style={{ fontSize: '12px', color: '#999' }}>
                          <span>{formatFileSize(Number(item.fileSize))}</span>
                          <span style={{ margin: '0 8px' }}>|</span>
                          <span>{formatDate(item.createdAt)}</span>
                        </div>
                      </div>
                      <a
                        href={`${(process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000').replace(/\/api\/?$/, '')}${item.fileUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '8px 16px',
                          background: '#1D2A5D',
                          color: '#fff',
                          borderRadius: '4px',
                          textDecoration: 'none',
                          fontSize: '14px',
                        }}
                      >
                        download
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {!downloadsLoading && downloads.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">No downloads available</p>
              </div>
            )}
            
            {loading && (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading...</p>
              </div>
            )}
            
            {!loading && downloads.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600">暂无下载资料</p>
              </div>
            )}
          </>
        }
      />
    </Layout>
  );
}

